import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { IPreferences, PreferencesContext } from 'contexts/preferences';
import { buildClassName } from 'utils/build-class-name';
import { useUserContext } from 'contexts/user';
import useKeyboard from 'hooks/use-keyboard';
import WarningIcon from 'components/icon/warning-icon';
import LoadingSpinner from 'components/loading-spinner';
import LocationIcon from 'components/icon/location-icon';
import HomeIcon from 'components/icon/home-icon';
import NotificationIcon from 'components/icon/notification-icon';
import ClockIcon from 'components/icon/clock-icon';
import StarIcon from 'components/icon/star-icon';
import { trackEvent } from 'utils/google-tag-manager';
import { testIds } from 'constants/test-constants';
import LocationDropdownSection from './location-dropdown-section';
import pushToRoute from 'utils/push-to-route';
import { useFeaturesContext, useThemeContext } from 'contexts';
import styles from './style.module.scss';
import { MapType } from 'data/search-predictions';
import useMapLocationChanger from 'hooks/use-map-location-changer';
import { ThemeNames } from 'types/themes';
import usePopularCities from 'hooks/use-popular-cities';
import { MILITARY_BASES_SEARCH_PREDICTION_GROUP } from 'data/search-predictions';
import { useTranslation } from 'next-i18next';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';

import type SearchPrediction from 'data/search-predictions';

export type SearchSuggestions = {
  identifier: number;
  label: string;
  latitude?: number;
  longitude?: number;
  urlPath: string;
  hasAreaPage?: boolean;
  zoom: number;
};

export type SearchAgentPrediction = {
  identifier: number;
  label: string;
  group: string;
};

export type SearchResult = SearchPrediction | SearchSuggestions;

interface Props {
  searchPredictions: SearchPrediction[];
  setActivePrediction: (searchPrediction: any) => void;
  isActive: boolean;
  isLoading: boolean;
  isAreaSearch?: boolean;
  isAdvancedSearch?: boolean;
  locationQuery?: string;
  onClick: (searchPrediction: SearchPrediction) => void;
  className?: string;
  isTyping?: boolean;
  disablePopularSearches?: boolean;
  hideSearchPanel?: () => void;
  searchAgentPredictions?: SearchAgentPrediction[];
  fullWidth?: boolean;
}

export function sortSearchPredictions(searchPredictions: SearchPrediction[]) {
  const locations = searchPredictions.filter(
    searchPrediction => searchPrediction.group === 'locations'
  );
  const listings = searchPredictions.filter(
    searchPrediction => searchPrediction.group === 'listings'
  );
  const schools = searchPredictions.filter(
    searchPrediction => searchPrediction.group === 'schools'
  );
  const buildings = searchPredictions.filter(
    searchPrediction => searchPrediction.group === 'buildings'
  );
  const militaryBases = searchPredictions.filter(
    searchPrediction => searchPrediction.group === MILITARY_BASES_SEARCH_PREDICTION_GROUP
  );
  [...militaryBases, ...locations, ...listings, ...schools, ...buildings].forEach(
    (searchPrediction, index) => (searchPrediction.identifier = index)
  );
  return {
    locations,
    listings,
    schools,
    militaryBases,
  };
}

export default function SuggestedLocationDropdown({
  searchPredictions,
  setActivePrediction,
  isActive,
  isAreaSearch,
  isAdvancedSearch,
  locationQuery,
  isLoading,
  onClick,
  className,
  isTyping,
  hideSearchPanel,
  disablePopularSearches,
  searchAgentPredictions = [],
  fullWidth = false,
}: Props) {
  const { t } = useTranslation(Namespaces.SEARCH);
  const { recentSearches } = useContext(PreferencesContext) as IPreferences;
  const { features } = useFeaturesContext();
  const { themeName } = useThemeContext();
  const router = useRouter();
  const isHomeAppraisal =
    router.asPath && router.asPath.includes('/home-appraisal');
  const [suggestedResults, setSuggestedResults] = useState(
    sortSearchPredictions(searchPredictions)
  );
  const [selectedResult, setSelectedResult] = useState(0);
  const [resultHasContent, setResultHasContent] = useState(false);
  const { user, siteLocation, userPopularCities } = useUserContext();
  const updatedPopularSearches = usePopularCities();
  const [updatedRecentSearches, setUpdatedRecentSearches] =
    useState(recentSearches);
  const [isEnterKeyPressed, setIsEnterKeyPressed] = useState(false);
  const showRecentSearches =
    isAdvancedSearch &&
    !locationQuery &&
    recentSearches &&
    !resultHasContent &&
    !isLoading &&
    !isAreaSearch;

  const notYetSearched =
    isAdvancedSearch && !locationQuery && !resultHasContent && !isLoading;
  const noResults = !resultHasContent && !isLoading && locationQuery;
  const showPopularSearches =
    (noResults || notYetSearched) && !disablePopularSearches;
  const showAgentPredictionSearches = !!searchAgentPredictions.length;

  const { listingParams } = useContext(PreferencesContext) as IPreferences;
  const map: MapType = useMapLocationChanger();

  // Create a helper function to consistently flatten and order search results
  const getFlattenedResults = (
    suggestedResults: ReturnType<typeof sortSearchPredictions>,
    recentSearches: SearchPrediction[] = [],
    popularSearches: SearchSuggestions[] = [],
    agentPredictions: SearchAgentPrediction[] = []
  ) => {
    return [
      ...suggestedResults.militaryBases,
      ...suggestedResults.locations,
      ...suggestedResults.listings,
      ...suggestedResults.schools,
      ...[...new Set(showRecentSearches ? recentSearches : [])],
      ...[...new Set(showPopularSearches ? popularSearches : [])],
      ...[...new Set(showAgentPredictionSearches ? agentPredictions : [])],
    ];
  };

  const [flattenedResults, setFlattenedResults] = useState<any>(
    getFlattenedResults(
      suggestedResults,
      updatedRecentSearches as SearchPrediction[],
      updatedPopularSearches,
      searchAgentPredictions
    )
  );

  useEffect(() => {
    updatedSearchesWithIdentifier();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecentSearches, siteLocation, userPopularCities]);

  useEffect(() => {
    setSuggestedResults(sortSearchPredictions(searchPredictions));

    if (locationQuery?.length && !searchPredictions.length && !isLoading) {
      trackEvent('no-results', 'search-results', locationQuery);
    }

    setResultHasContent(
      (!!searchPredictions.length || !!searchAgentPredictions.length) &&
        !isLoading
    );
    setActivePrediction(
      flattenedResults.filter(
        (item: SearchResult) =>
          item.identifier === selectedResult ||
          item.identifier === selectedResult
      )[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPredictions, searchAgentPredictions, isLoading]);

  useEffect(() => {
    setFlattenedResults(
      getFlattenedResults(
        suggestedResults,
        updatedRecentSearches as SearchPrediction[],
        updatedPopularSearches,
        searchAgentPredictions
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedResults]);

  useEffect(() => {
    if (flattenedResults.length > 0) {
      setActivePrediction(flattenedResults[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flattenedResults]);

  useEffect(() => {
    setActivePrediction(
      Object.values(flattenedResults as SearchResult).filter(
        ({ identifier }) => identifier === selectedResult
      )[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResult]);

  useEffect(() => {
    if (isEnterKeyPressed) {
      navigateToEnteredLocation();
      setIsEnterKeyPressed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnterKeyPressed]);

  const updatedSearchesWithIdentifier = () => {
    let tempRecentSearch = recentSearches;
    if (isHomeAppraisal)
      tempRecentSearch = recentSearches?.filter(s => s.label !== 'city');
    const updatedRecentSearches = tempRecentSearch?.map((search, index) => {
      return {
        ...search,
        identifier: index,
      };
    }) as SearchPrediction[];
    const updatedPopularSearchesWithId = updatedPopularSearches?.map(
      (search, index) => {
        return {
          ...search,
          identifier: (tempRecentSearch?.length || 0) + index,
        };
      }
    );
    setUpdatedRecentSearches(updatedRecentSearches);
    setFlattenedResults(
      getFlattenedResults(
        suggestedResults,
        updatedRecentSearches,
        updatedPopularSearchesWithId,
        searchAgentPredictions
      )
    );
  };

  const navigateDownListWithKeyboard = () => {
    if (isActive) {
      setSelectedResult(prev =>
        Math.min(prev + 1, flattenedResults.length - 1)
      );
    }
  };
  const navigateUpListWithKeyboard = () => {
    if (isActive) {
      setSelectedResult(prev => Math.max(prev - 1, 0));
    }
  };
  const navigateToEnteredLocation = () => {
    if (isActive) {
      const selectedLocation = Object.values(
        flattenedResults as SearchResult
      ).filter(({ identifier }) => identifier === selectedResult)[0];
      if (selectedLocation) {
        setActivePrediction(selectedLocation);
        const isSearchSuggestion =
          (selectedLocation as Record<string, unknown>)['group'] === undefined;
        if (isSearchSuggestion) {
          hideSearchPanel?.();
          pushToRoute(
            selectedLocation as SearchSuggestions,
            listingParams,
            router,
            themeName as ThemeNames,
            map,
            features.useUsListings
          );
        } else {
          onClick(selectedLocation as SearchPrediction);
        }
      }
    }
  };

  useKeyboard(navigateDownListWithKeyboard, 'arrowdown');
  useKeyboard(navigateUpListWithKeyboard, 'arrowup');
  useKeyboard(() => {
    const input: HTMLInputElement | null =
      document.querySelector('#locationInput');
    // Needed to get input value this way because locationQuery gets emptied for some reason when using the keyboard hook
    if (!input?.value.length) {
      setIsEnterKeyPressed(true);
    } else {
      resultHasContent &&
        !isLoading &&
        !!searchPredictions.length &&
        !isTyping &&
        setIsEnterKeyPressed(true);
    }
  }, 'enter');

  const handlePushToRoute = (searchSuggestion: SearchSuggestions) => {
    hideSearchPanel?.();
    pushToRoute(
      searchSuggestion,
      listingParams,
      router,
      themeName as ThemeNames,
      map,
      features.useUsListings,
      user!
    );
  };

  return !isActive ? null : (
    <div
      className={buildClassName(
        styles.component,
        className,
        isAreaSearch && styles['component-small-width'],
        fullWidth && styles['component-full-width'],
        isAdvancedSearch && styles['isAdvancedSearch']
      )}
      data-testid={testIds.suggestedLocationsDropdown}
    >
      {isLoading && <LoadingSpinner className={styles['loading-spinner']} />}
      {noResults && locationQuery.length > 2 && !isTyping && (
        <span
          className={styles['warning']}
        >{t(LOCALE_SEARCH_PATHS.DROPDOWN_NO_RESULTS, { 
            defaultValue: 'Sorry, there are no results for "{{query}}"',
            query: locationQuery, 
          })}</span>
      )}
      {resultHasContent && (
        <>
          {suggestedResults.militaryBases.length > 0 && (
            <LocationDropdownSection
              title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_MILITARY_BASES, { defaultValue: 'Military Bases' })}
              Icon={LocationIcon}
              selectedResult={selectedResult}
              searchPredictions={suggestedResults.militaryBases}
              onClick={onClick}
            />
          )}
          {!isAreaSearch && (
            <LocationDropdownSection
              title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_LISTINGS, { defaultValue: 'Listings' })}
              Icon={HomeIcon}
              selectedResult={selectedResult}
              searchPredictions={suggestedResults.listings}
              onClick={onClick}
            />
          )}
          <LocationDropdownSection
            title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_LOCATIONS, { defaultValue: 'Locations' })}
            Icon={LocationIcon}
            selectedResult={selectedResult}
            searchPredictions={suggestedResults.locations}
            onClick={onClick}
          />
          <LocationDropdownSection
            title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_SCHOOLS, { defaultValue: 'Schools' })}
            Icon={NotificationIcon}
            selectedResult={selectedResult}
            searchPredictions={suggestedResults.schools}
            onClick={onClick}
          />
        </>
      )}
      {showRecentSearches && (
        <LocationDropdownSection
          title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_RECENT_SEARCHES, { defaultValue: 'Recent Searches' })}
          Icon={ClockIcon}
          selectedResult={selectedResult}
          searchPredictions={updatedRecentSearches as SearchPrediction[]}
          onClick={onClick}
        />
      )}
      {showPopularSearches && (
        <LocationDropdownSection
          title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_POPULAR_SEARCHES, { defaultValue: 'Popular Searches' })}
          Icon={StarIcon}
          selectedResult={selectedResult}
          searchPredictions={[]}
          searchSuggestions={updatedPopularSearches}
          onClick={onClick}
          pushToRoute={handlePushToRoute}
        />
      )}
      {showAgentPredictionSearches && (
        <LocationDropdownSection
          title={t(LOCALE_SEARCH_PATHS.DROPDOWN_SECTION_LOCATIONS, { defaultValue: 'Locations' })}
          Icon={StarIcon}
          selectedResult={selectedResult}
          searchPredictions={[]}
          searchAgentPredictions={searchAgentPredictions}
          onClick={onClick}
          pushToRoute={handlePushToRoute}
        />
      )}
      {(!showPopularSearches ||
        (noResults && locationQuery.length > 2 && !isTyping)) && (
        <span className={styles['warning']}>
          <WarningIcon />
          {t(LOCALE_SEARCH_PATHS.DROPDOWN_OUT_OF_SERVICE_AREA, { 
            defaultValue: "Don't see what you're looking for? Your search might be out of our service area",
          })}
        </span>
      )}
    </div>
  );
}
